import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AnalyticsOnlineService } from "../../_services/online/analytics-powerbi-online.service";
import { SpinnerService } from "../../_services/spinner.service";
import { Component, Inject } from "@angular/core";
import { CreatePowerBIBookmarkDetail, PowerBIBookmark } from "../../_objects/analytics-powerbi-bookmark";
import { Report } from "powerbi-client";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { uniqueBookmarkNameValidator } from "../validators/bookmark-validators";

@Component({
  selector: "app-bookmarks-dialog",
  templateUrl: "./bookmarks-dialog.component.html",
  styleUrls: ["./bookmarks-dialog.component.scss"],
  standalone: false
})
export class BookmarksDialogComponent {
  existingBookmarks: PowerBIBookmark[] = [];
  filteredBookmarks: PowerBIBookmark[] = [];
  selectedBookmark: PowerBIBookmark = null;
  hoveredBookmark: PowerBIBookmark = null;
  report: Report;
  sharedUrl: string;
  sharedId: string;
  searchText: string;
  bookmarkForm: FormGroup;

  constructor(
    public translate: TranslateService,
    public dialogRef: MatDialogRef<BookmarksDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private oscSpinnerService: SpinnerService,
    private analyticsService: AnalyticsOnlineService,
    private router: Router,
    private fb: FormBuilder
  ) {
    this.sharedId = data.sharedId;
    this.report = data.report;
    this.initializeForm();

    this.getAllBookmarks();
  }

  initializeForm() {
    this.bookmarkForm = this.fb.group({
      newBookmarkName: [
        "",
        {
          validators: [Validators.required, uniqueBookmarkNameValidator(() => this.existingBookmarks)],
          updateOn: "change"
        }
      ]
    });
  }

  get newBookmarkName() {
    return this.bookmarkForm.get("newBookmarkName");
  }

  saveBookmark() {
    if (this.bookmarkForm.invalid) return;
    this.oscSpinnerService.show();
    this.report.bookmarksManager.capture({ personalizeVisuals: true }).then(
      (bookmark) => {
        const powerBiBookmark: CreatePowerBIBookmarkDetail = {
          bookmarkName: this.newBookmarkName.value,
          bookmarkState: bookmark.state
        };
        this.analyticsService.createBookmark(powerBiBookmark).subscribe((bookmark) => {
          this.existingBookmarks.push(bookmark);
          this.selectedBookmark = bookmark;
          this.oscSpinnerService.hide();
        });
      },
      (errors) => {
        this.oscSpinnerService.hide();
        console.error(errors);
      }
    );
  }

  updateFilter() {
    this.filteredBookmarks = this.existingBookmarks.filter((bookmark) => bookmark.bookmarkName.toLowerCase().includes(this.searchText.toLowerCase()));
  }

  selectBookmark(bookmark: PowerBIBookmark) {
    this.selectedBookmark = bookmark;
    this.updateSharedUrl();
  }

  hoverBookmark(bookmark: PowerBIBookmark | null) {
    this.hoveredBookmark = bookmark;
  }

  highlightRow(bookmark: PowerBIBookmark) {
    return bookmark === this.selectedBookmark || bookmark === this.hoveredBookmark;
  }

  openBookmark(bookmark: PowerBIBookmark) {
    this.dialogRef.close({ bookmarkState: bookmark.bookmarkState });
  }

  deleteBookmark(bookmark: PowerBIBookmark) {
    this.oscSpinnerService.show();
    this.analyticsService.deleteBookmark(bookmark.id).subscribe(() => {
      this.getAllBookmarks();
    });
  }

  async copyBookmark() {
    await navigator.clipboard.writeText(this.sharedUrl);
  }

  private updateSharedUrl() {
    if (this.selectedBookmark) {
      this.sharedUrl =
        this.sharedId === undefined
          ? `${window.location.origin}${this.router.url}/${this.selectedBookmark.id}`
          : `${window.location.origin}${this.getUrlWithoutParams(this.router.url)}/${this.selectedBookmark.id}`;
    }
  }

  private getAllBookmarks() {
    this.analyticsService.getBookmarks().subscribe((bookmarks) => {
      this.existingBookmarks = this.filteredBookmarks = bookmarks;
      this.newBookmarkName.updateValueAndValidity();
      if (this.sharedId) {
        this.selectedBookmark = this.existingBookmarks.find((bookmark) => bookmark.id === this.sharedId);
        this.updateSharedUrl();
        this.sharedId = null;
      }
      this.oscSpinnerService.hide();
    });
  }

  private getUrlWithoutParams(originalUrl: string): string {
    const lastIndex = originalUrl.lastIndexOf("/");
    return originalUrl.substring(0, lastIndex);
  }

  close() {
    this.dialogRef.close();
  }
}
